import { Box, Button, Chip, Divider, IconButton, Stack, TextField, Typography } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { Controller, FormProvider, useFieldArray, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import DeleteIcon from '@mui/icons-material/Delete'
import { UpdateContentInput } from '@typings/graphql'
import { convertToTextObject } from '@utils/texts'
import { FormFooterBar } from '@features/cms/components/ui/FormFooterBar'
import FileDragDrop from '@features/cms/components/FileDragDrop'
import { useUnsavedChangesAlert } from '@hooks/useUnsavedChangesAlert'

import { deleteTexts } from '../mutation-helper/text'
import { updateContent } from '../mutation-helper/content'
import { ConfigAndStyleEditor } from '../components/ConfigAndStyleEditor'
import { InteractionCardEditor, InteractionCardFormInputs, interactionCardTextKeys, EditableFile } from '../components/InteractionCardEditor'
import { ContentFile, uploadFile } from '../mutation-helper/file'
import { ContentBlockEditor } from '../components/ContentBlockEditor'
import { videoQuizConfigSchema } from '../config-schemas/default/videoQuiz'
import { VisibilityEditor } from '../components/VisibilityEditor'

import { ContentEditorComponentProps } from '.'

type UpdateVideoQuizContentFormInput = InteractionCardFormInputs & {
  title: string,
  question: string,
  solution: string,
  texts: {
    key: string,
    value: string,
  }[],
  backgroundImage: EditableFile,
  questionVideo: EditableFile,
  solutionVideo?: EditableFile,
  hidden: boolean,
  blockedById: string | null,
  style: string,
  config: string
}

const staticTextKeys = ['title', 'question', 'solution'] as const

export const VideoQuizContentEditor: React.FC<ContentEditorComponentProps> = ({ nuggetId, content, blocksContent, refetch, onEdited }) => {
  const { t } = useTranslation()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<unknown | undefined>(undefined)
  const [showSnackbar, setShowSnackbar] = useState(false)
  const [deletedTextKeys, setDeletedTextKeys] = useState<string[]>([])

  const texts = useMemo(() => {
    const textKeys = Object.keys(content.texts).filter((key) => !interactionCardTextKeys.includes(key) && !staticTextKeys.includes(key))

    return textKeys.length
      ? textKeys.map(key => ({
        key,
        value: content.texts[key]
      }))
      : [{
          key: '',
          value: ''
        }]
  }, [content.texts])

  const getDefaultValues = () => {
    return {
      interactionCardTexts: interactionCardTextKeys.map((key) => ({
        key,
        value: content.texts[key] || ''
      })),
      descriptionImage: {
        key: 'description',
        file: content.files.find((file: ContentFile) => file.key === 'description') || null,
        replaceId: null
      },
      backgroundImage: {
        key: 'background',
        file: content.files.find((file: ContentFile) => file.key === 'background') || null,
        replaceId: null
      },
      questionVideo: {
        key: 'question',
        file: content.files.find((file: ContentFile) => file.key === 'question') || null,
        replaceId: null
      },
      solutionVideo: {
        key: 'solution',
        file: content.files.find((file: ContentFile) => file.key === 'solution') || null,
        replaceId: null
      },
      title: content.texts.title || '',
      question: content.texts.question || '',
      solution: content.texts.solution || '',
      texts,
      blockedById: content.blockedById || null,
      hidden: content.hidden ?? false,
      config: JSON.stringify(content.config, null, '\t'),
      style: JSON.stringify(content.style, null, '\t') || ''
    }
  }

  const methods = useForm<UpdateVideoQuizContentFormInput>({
    defaultValues: getDefaultValues()
  })

  useUnsavedChangesAlert(methods.formState.isDirty)

  useEffect(() => {
    onEdited?.(methods.formState.isDirty)
  }, [methods.formState.isDirty])

  const { fields: textFields, append: appendText, remove: removeText } = useFieldArray({
    control: methods.control,
    name: 'texts'
  })

  const deleteText = (index: number, key: string) => {
    removeText(index)
    setDeletedTextKeys((prev) => [...prev, key])
  }

  const textInputs = useMemo(() => {
    return textFields.map((textField, index) => (
      <Box display="flex" key={index} gap={2}>
        <Controller
          control={methods.control}
          name={`texts.${index}.key`}
          rules={{ required: true }}
          render={({ field: { value, onChange }, fieldState: { error: keyError } }) =>
            <TextField
              value={value}
              onChange={onChange}
              disabled={content.texts[textField.key] !== undefined}
              error={!!keyError}
              sx={{ flex: 1 }}
              label={t('common.key')}
              InputLabelProps={{ shrink: true }}
              helperText={!!keyError && t('common.required')}
            />
            }
        />
        <Controller
          control={methods.control}
          name={`texts.${index}.value`}
          rules={{ required: true }}
          render={({ field: { onChange }, fieldState: { error: valueError } }) =>
            <TextField
              value={methods.watch(`texts.${index}.value`)}
              onChange={onChange}
              error={!!valueError}
              sx={{ flex: 1 }}
              label={t('common.text')}
              InputLabelProps={{ shrink: true }}
              helperText={!!valueError && t('common.required')}
            />}
        />
        <IconButton onClick={() => deleteText(index, textField.key)}>
          <DeleteIcon></DeleteIcon>
        </IconButton>
      </Box>
    ))
  }, [textFields])

  const onSubmit = async (submittedData: UpdateVideoQuizContentFormInput) => {
    setLoading(true)

    if (submittedData.backgroundImage.file && !(submittedData.backgroundImage.file as any)?.id) {
      try {
        await uploadFile({
          file: submittedData.backgroundImage.file,
          data: {
            key: 'background',
            replace: true,
            model: 'Content',
            modelId: content.id
          }
        })
      } catch (e) {
        setError(e)
      }
    }

    if (submittedData.descriptionImage.file && !(submittedData.descriptionImage.file as any)?.id) {
      try {
        await uploadFile({
          file: submittedData.descriptionImage.file,
          data: {
            key: 'description',
            replace: true,
            model: 'Content',
            modelId: content.id
          }
        })
      } catch (e) {
        setError(e)
      }
    }

    if (submittedData.questionVideo.file && !(submittedData.questionVideo.file as any)?.id) {
      try {
        await uploadFile({
          file: submittedData.questionVideo.file,
          data: {
            key: 'question',
            replace: true,
            model: 'Content',
            modelId: content.id
          }
        })
      } catch (e) {
        setError(e)
      }
    }

    if (submittedData.solutionVideo?.file && !(submittedData.solutionVideo?.file as any)?.id) {
      try {
        await uploadFile({
          file: submittedData.solutionVideo.file,
          data: {
            key: 'solution',
            replace: true,
            model: 'Content',
            modelId: content.id
          }
        })
      } catch (e) {
        setError(e)
      }
    }

    const updateTextVariables = submittedData.texts.concat(submittedData.interactionCardTexts)
      .filter(text => !!text.value)
      .map((text) => convertToTextObject(text.key, text.value))

    staticTextKeys.forEach((key) => {
      updateTextVariables.push(convertToTextObject(key, submittedData[key]))
    })

    const emptyTextsKeys = updateTextVariables.filter(text => !text.value).map((text) => text.key)

    if (deletedTextKeys.length || emptyTextsKeys.length) {
      const deletedTextsVariables = deletedTextKeys.concat(emptyTextsKeys).map((key) => ({
        key,
        model: 'Content',
        modelId: content.id
      }))

      try {
        await deleteTexts(deletedTextsVariables)
      } catch (e) {
        setError(e)
      }
    }

    const data: UpdateContentInput = {
      nuggetId,
      order: content.order,
      type: content.type,
      blockedById: submittedData.blockedById || null,
      hidden: submittedData.hidden,
      config: JSON.parse(submittedData.config),
      style: JSON.parse(submittedData.style),
      texts: updateTextVariables.filter(text => !!text.value)
    }

    try {
      await updateContent(content.id, data)
      methods.reset()
    } catch (e) {
      setError(e)
    }

    setLoading(false)
    setShowSnackbar(true)
    refetch && refetch()
  }

  useEffect(() => {
    methods.reset(getDefaultValues())
  }, [content])

  const addImage = (file: File) => {
    methods.setValue('backgroundImage.file', file, { shouldDirty: true })
  }

  const addVideo = (file: File, key: 'question' | 'solution') => {
    methods.setValue(`${key}Video.file`, file, { shouldDirty: true })
  }

  const closeSnackbar = () => {
    setShowSnackbar(false)
    setError(undefined)
  }

  return <FormProvider {...methods}>
    <form
      style={{ display: 'flex', flex: 1, flexDirection: 'column', overflowY: 'hidden' }}
      onSubmit={methods.handleSubmit(onSubmit)}
    >
      <Stack spacing={2} p={4} flex={1} sx={{ overflowY: 'auto' }}>
        <InteractionCardEditor content={content} />
        <Box>
          <Chip label="Hintergrund" size="small" sx={{ marginBottom: '8px' }} />
          <FileDragDrop
            accept={{ 'image/*': ['.png', '.jpg', '.jpeg', '.webp'] }}
            height="200px"
            preview
            onFileChanged={addImage}
          >
            {!content?.files.find((file: ContentFile) => file.key === 'background')
              ? <Typography>{t('edit.poi.dragImage')}</Typography>
              : <Box width="100%"
                  height="100%"
                  sx={{
                    backgroundImage: `url(${content.files.find((file: ContentFile) => file.key === 'background')?.url})`,
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: 'contain',
                    backgroundPosition: 'center center'
                  }}
              />}
          </FileDragDrop>
        </Box>
        <Box marginBottom="60px">
          <ConfigAndStyleEditor content={content} schema={videoQuizConfigSchema} />
        </Box>
        <Box>
          <Chip label={t('edit.content.question')} size="small" sx={{ marginBottom: '8px' }} />
          <FileDragDrop
            accept={{ 'video/*': ['.mp4'] }}
            height="200px"
            preview
            onFileChanged={(file) => addVideo(file, 'question')}
          >
            {!content?.files.find((file: ContentFile) => file.key === 'question')
              ? <Typography>{t('edit.poi.dragVideo')}</Typography>
              : <Box width="100%"
                  height="100%"
                  sx={{
                    backgroundImage: `url(${content.files.find((file: ContentFile) => file.key === 'question')?.url})`,
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: 'contain',
                    backgroundPosition: 'center center'
                  }}
              />}
          </FileDragDrop>
        </Box>
        <Box>
          <Chip label={t('edit.content.solution')} size="small" sx={{ marginBottom: '8px' }} />
          <FileDragDrop
            accept={{ 'video/*': ['.mp4'] }}
            height="200px"
            preview
            onFileChanged={(file) => addVideo(file, 'solution')}
          >
            {!content?.files.find((file: ContentFile) => file.key === 'solution')
              ? <Typography>{t('edit.poi.dragVideo')}</Typography>
              : <Box width="100%"
                  height="100%"
                  sx={{
                    backgroundImage: `url(${content.files.find((file: ContentFile) => file.key === 'solution')?.url})`,
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: 'contain',
                    backgroundPosition: 'center center'
                  }}
              />}
          </FileDragDrop>
        </Box>
        <TextField
          label={t('common.title')}
          {...methods.register('title', { required: true })}
        ></TextField>
        <TextField
          label={t('edit.content.question')}
          {...methods.register('question', { required: true })}
        ></TextField>
        <TextField
          label={t('edit.content.solution')}
          {...methods.register('solution', { required: true })}
        ></TextField>
        <Divider />
        {textInputs}
        <Box width="600xpx">
          <Button
            variant="outlined"
            sx={{ my: 0.5 }}
            onClick={() => appendText({ key: '', value: '' })}
          >
            <AddCircleOutlineIcon></AddCircleOutlineIcon>
          </Button>
        </Box>
        <ContentBlockEditor content={content} />
        <VisibilityEditor blocked={blocksContent} />
      </Stack>
      <FormFooterBar
        disabled={!methods.formState.isDirty && methods.formState.isValid}
        loading={loading}
        uploadError={error}
        showSnackbar={showSnackbar}
        closeSnackbar={closeSnackbar}
      />
    </form>
  </FormProvider>
}
